import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { CSVLink } from 'react-csv';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const csvHeaders = [
  { label: 'Date', key: 'inv_date' },
  { label: 'Username', key: 'username' },
  { label: 'Total Sales', key: 'Total' },
];

const formatCurrency = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return value ?? '-';
  }
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMonthBounds = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    start: formatLocalDate(start),
    end: formatLocalDate(today),
  };
};

const SalesReport = () => {
  const monthBounds = useMemo(() => getMonthBounds(), []);
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [username, setUsername] = useState('');
  const [fromDate, setFromDate] = useState(monthBounds.start);
  const [toDate, setToDate] = useState(monthBounds.end);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    const fetchSalesData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get('https://serverdemo.ccsoftwares.com/sales/getdailysales');
        const enriched = (response.data || []).map((record) => {
          const createdAt = record.inv_date ? new Date(record.inv_date) : null;
          return {
            ...record,
            dateObj: createdAt,
          };
        });
        enriched.sort((a, b) => {
          if (a.dateObj && b.dateObj) {
            return b.dateObj - a.dateObj;
          }
          if (a.dateObj) return -1;
          if (b.dateObj) return 1;
          return 0;
        });
        setSalesData(enriched);
      } catch (err) {
        console.error('Failed to load sales report', err);
        setError('Unable to load sales data at the moment.');
      } finally {
        setLoading(false);
      }
    };

    fetchSalesData();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [username, fromDate, toDate]);

  const filteredData = useMemo(() => {
    if (!salesData.length) return [];

    const normalizedUsername = username.trim().toLowerCase();
    const from = fromDate ? new Date(fromDate) : null;
    if (from) {
      from.setHours(0, 0, 0, 0);
    }
    const to = toDate ? new Date(toDate) : null;
    if (to) {
      to.setHours(23, 59, 59, 999);
    }

    return salesData.filter((record) => {
      const matchesUsername = normalizedUsername
        ? record.username?.toLowerCase().includes(normalizedUsername)
        : true;

      if (!matchesUsername) {
        return false;
      }

      if (!from && !to) {
        return true;
      }

      if (!record.dateObj) {
        return false;
      }

      const afterFrom = from ? record.dateObj >= from : true;
      const beforeTo = to ? record.dateObj <= to : true;

      return afterFrom && beforeTo;
    });
  }, [salesData, username, fromDate, toDate]);

  const todaysSales = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return filteredData.reduce((accumulator, record) => {
      if (!record.dateObj) return accumulator;
      const recordDate = new Date(record.dateObj);
      recordDate.setHours(0, 0, 0, 0);
      if (recordDate.getTime() === today.getTime()) {
        return accumulator + Number(record.Total || 0);
      }
      return accumulator;
    }, 0);
  }, [filteredData]);

  const selectedPeriodSales = useMemo(
    () =>
      filteredData.reduce(
        (accumulator, record) => accumulator + Number(record.Total || 0),
        0
      ),
    [filteredData]
  );

  const uniqueUsersCount = useMemo(() => {
    const uniqueUsers = new Set(filteredData.map((record) => record.username));
    return uniqueUsers.size;
  }, [filteredData]);

  const paginatedSales = useMemo(() => {
    if (rowsPerPage === -1) {
      return filteredData;
    }
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const csvData = useMemo(
    () =>
      filteredData.map((record) => ({
        ...record,
        Total: Number(record.Total || 0).toFixed(2),
      })),
    [filteredData]
  );

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Prepare chart data
  const groupedByDate = useMemo(() => {
    const grouping = {};
    filteredData.forEach(({ inv_date, username: user, Total }) => {
      if (!grouping[inv_date]) {
        grouping[inv_date] = {};
      }
      grouping[inv_date][user] = (grouping[inv_date][user] || 0) + Number(Total || 0);
    });
    return grouping;
  }, [filteredData]);

  const uniqueUsers = useMemo(
    () => [...new Set(filteredData.map((record) => record.username))],
    [filteredData]
  );

  const clusteredChartData = useMemo(
    () =>
      Object.keys(groupedByDate).map((date) => {
        const row = { date };
        uniqueUsers.forEach((user) => {
          row[user] = groupedByDate[date][user] || 0;
        });
        return row;
      }),
    [groupedByDate, uniqueUsers]
  );

  const dailyTotals = useMemo(() => {
    const totals = {};
    filteredData.forEach(({ inv_date, Total }) => {
      totals[inv_date] = (totals[inv_date] || 0) + Number(Total || 0);
    });
    return Object.keys(totals).map((date) => ({
      date,
      totalSales: totals[date],
    }));
  }, [filteredData]);

  const chartColours = ['#3f51b5', '#ff5722', '#4caf50', '#fbc02d', '#9c27b0', '#009688'];

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            User-wise Sales Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Analyse total sales by user with date and user name filters.
          </Typography>
        </Box>
        <CSVLink
          data={csvData}
          headers={csvHeaders}
          filename="user-sales-report.csv"
          style={{ textDecoration: 'none' }}
        >
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{ textTransform: 'none', borderRadius: 2 }}
            disabled={!filteredData.length}
          >
            Download CSV
          </Button>
        </CSVLink>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Today&apos;s Sales
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              Rs. {formatCurrency(todaysSales)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Selected Period Sales
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              Rs. {formatCurrency(selectedPeriodSales)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Active Users (Filtered)
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              {uniqueUsersCount.toLocaleString()}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Search Username"
              placeholder="Start typing a username"
              fullWidth
              value={username}
              onChange={(event) => setUsername(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#000000' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Username</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Total Sales (Rs.)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedSales.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No sales records match your filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedSales.map((row, index) => (
                  <TableRow key={`${row.inv_date}-${row.username}-${index}`}>
                    <TableCell>{row.inv_date}</TableCell>
                    <TableCell>{row.username}</TableCell>
                    <TableCell>{formatCurrency(row.Total)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && !error && (
        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
          sx={{ mt: 2 }}
        />
      )}

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          User Contribution by Date
        </Typography>
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={clusteredChartData}>
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              {uniqueUsers.map((user, index) => (
                <Bar key={user} dataKey={user} fill={chartColours[index % chartColours.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Daily Combined Sales (Rs.)
        </Typography>
        <Paper sx={{ p: 2, borderRadius: 2, mb: 3 }}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dailyTotals}>
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="totalSales" fill="#1976d2" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>

        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#000000' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Total Sales (Rs.)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dailyTotals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No daily totals available for the selected filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                dailyTotals.map((row) => (
                  <TableRow key={row.date}>
                    <TableCell>{row.date}</TableCell>
                    <TableCell>{formatCurrency(row.totalSales)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default SalesReport;
